import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../core/rootHooks';
import { setFeature, setSelection as setPageSelection, setSubtab, setTab } from './pageContextSlice';
import { EOutputType, ESelectionType } from '../shared/sharedEnums';
import { selectSelectionData, selectSubscribedVesselSearchFormData } from '../features/search/searchSelectors';
import { setSelectedVesselsForOutputRequest } from '../features/search/searchSlice';
import { filterGivenVesselsByFilterCriteriaForOutput } from '../features/search/helper';
import { displayOutputByPortfolioIdRequest } from '../features/portfolio/portfolioSlice';
import { displayOutputByFilterIdRequest } from '../features/filter/filterSlice';
import { IPortFormData, IVesselFormData } from '../shared/sharedTypes';
import type { IVesselSearchForm } from '../features/search/searchTypes';
import { RouteParams, SelectionFromRoute } from './routingTypes';
import {
	buildPathname,
	decodeParam,
	encodeParam,
	normalizeSelectionType,
	parseSelectionFromState,
	selectionsMatch,
} from './routingHelpers';
import { getPortGeometryByNameAndCountryRequest } from '../features/port/portSlice';
import { isNil } from 'lodash';

/**
 * Keeps the browser URL, route params, and redux selection state in sync so that
 * back/forward navigation restores feature + selection context (vessel, portfolio, sector, etc).
 */
const RouteStateManager = () => {
	const { feature, tab, subtab, selectionType: selectionTypeParam, selectionParam } = useParams<RouteParams>();

	const location = useLocation();
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const dispatch = useAppDispatch();

	const subscribedVessels = useAppSelector(selectSubscribedVesselSearchFormData);
	const selectionData = useAppSelector(selectSelectionData);
	const pageContext = useAppSelector((state) => state.pageContextSlice);
	const lastHydratedRoute = useRef<string | undefined>(undefined);
	const hydratingFromRoute = useRef(false);
	const lastSelectionInStore = useRef<SelectionFromRoute>({});

	// console.log('--- START ---');
	// console.log('Current Location:', location.pathname);
	// console.log('Navigation Type:', navigationType);
	// console.log('Route Params:', { feature, tab, subtab, selectionTypeParam, selectionParam });

	const selectionFromRoute = useMemo<SelectionFromRoute>(() => {
		// Priority 1: Check if we have explicit selection parameters
		if (selectionTypeParam && selectionParam) {
			const type = normalizeSelectionType(selectionTypeParam);
			const param = decodeParam(selectionParam);
			return { type, param };
		}

		// Priority 2: Check if feature and tab form a selection (e.g., /Vessel/123)
		if (feature && tab && !subtab && !selectionTypeParam) {
			const type = normalizeSelectionType(feature);
			if (type) {
				const param = decodeParam(tab);
				return { type, param };
			}
		}

		// Priority 3: Handle direct selection routes (e.g., /Vessel/123) where the selection type is the first path segment
		if (!feature && !selectionTypeParam && !tab && !subtab) {
			const pathSegments = location.pathname.split('/').filter(Boolean);
			if (pathSegments.length === 2) {
				const [firstSegment, secondSegment] = pathSegments;
				const type = normalizeSelectionType(firstSegment);
				if (type && secondSegment) {
					const param = decodeParam(secondSegment);
					return { type, param };
				}
			}
		}

		return { type: undefined, param: undefined };
	}, [selectionParam, selectionTypeParam, feature, tab, subtab, location.pathname]);

	const lastLocationRef = useRef<string | null>(null);
	const isRealPopRef = useRef(false);

	useLayoutEffect(() => {
		if (navigationType === 'POP' && lastLocationRef.current && lastLocationRef.current !== location.pathname) {
			isRealPopRef.current = true;
		} else {
			isRealPopRef.current = false;
		}

		lastLocationRef.current = location.pathname;
	});

	// Keep page context slice aligned with the current route.
	useEffect(() => {
		// console.log('Syncing route params to Redux:', { feature, tab, subtab });
		dispatch(setFeature(feature || null));
		dispatch(setTab(tab));
		dispatch(setSubtab(subtab));
	}, [dispatch, feature, tab, subtab]);

	// Sync pageContext.selectionType with selectionData changes (from UI selections, not just route hydration)
	useEffect(() => {
		if (!subscribedVessels || subscribedVessels.length === 0) {
			return;
		}

		// Skip if we're currently hydrating from route to avoid conflicts
		if (hydratingFromRoute.current) {
			// console.log('Skipping selectionData sync: hydrating from route.');
			return;
		}

		// Update pageContext selection when selectionData changes (e.g., user selects vessel from UI)
		const currentSelectionType = selectionData.selectionType as ESelectionType;

		// Only update if the selection type actually changed
		if (pageContext.selectionType !== currentSelectionType) {
			// Get the appropriate selection ID based on selection type
			let selectionId: string | undefined;
			if (currentSelectionType === ESelectionType.Port) {
				selectionId =
					(selectionData.selectionTypeObject as IPortFormData).name +
					'_' +
					(selectionData.selectionTypeObject as IPortFormData).country;
			} else if (currentSelectionType === ESelectionType.Vessel) {
				selectionId = selectionData.selectedVesselsList?.[0]?.imoNumber?.toString();
			} else if (currentSelectionType === ESelectionType.Portfolio || currentSelectionType === ESelectionType.DraftPortfolio) {
				const portfolio = selectionData.selectionTypeObject as any;
				selectionId = portfolio?.id?.toString() ?? portfolio?.tempId;
			} else if (currentSelectionType === ESelectionType.Filter || currentSelectionType === ESelectionType.DraftFilter) {
				const filter = selectionData.selectionTypeObject as any;
				selectionId = filter?.id?.toString() ?? filter?.tempId;
			} else if (currentSelectionType === ESelectionType.Ownership || currentSelectionType === ESelectionType.Sector) {
				// For ownership/sector, the selectionName contains the actual value
				selectionId = selectionData.selectionName;
			}

			// console.log('Dispatching page selection from UI change:', { type: currentSelectionType, id: selectionId });
			dispatch(setPageSelection({ type: currentSelectionType, id: selectionId }));
			dispatch(setTab(undefined)); // Reset tab so DynamicTab can pick appropriate default for new selection
			dispatch(setSubtab(undefined)); // Reset subtab as well
		}
	}, [
		dispatch,
		selectionData.selectionType,
		selectionData.selectedVesselsList,
		selectionData.selectionTypeObject,
		selectionData.selectionName,
		pageContext.selectionType,
		subscribedVessels,
	]);

	// Hydrate redux selection state when the URL contains selection info.
	useEffect(() => {
		// console.log('Hydration Effect: URL to Redux');
		if (!subscribedVessels || subscribedVessels.length === 0) {
			// console.log('Hydration Effect: Skipped - no subscribed vessels.');
			return;
		}
		const { type, param } = selectionFromRoute;
		// console.log('Hydration Effect: Selection from route:', { type, param });

		const currentRouteSignature = `${location.pathname}`;

		// If route has no selection info (e.g., back to root), clear selection on POP only if really needed.
		if (!type) {
			if (isRealPopRef.current && selectionData.selectionType !== ESelectionType.None) {
				hydratingFromRoute.current = true;
				// console.log('Hydration Effect: Clearing selection on POP navigation to a route without selection.');

				// Clear page context selection
				dispatch(setPageSelection({ type: ESelectionType.None, id: undefined }));

				// Clear vessels selection data
				const outputSelection = {
					selectedVesselsList: subscribedVessels.map((v) => ({
						name: v.name,
						imoNumber: v.imoNumber,
						sectorName: v.sector,
						companyName: v.companyName,
					})) as IVesselFormData[], // Keep all vessels, just clear the selection
					outputType:
						subscribedVessels.length <= 0 ?
							EOutputType.None :
						subscribedVessels.length === 1
							? EOutputType.Vessel
							: subscribedVessels.length <= 200
								? EOutputType.Fleet
								: EOutputType.Summary,
					selectionType: ESelectionType.None,
					selectionName: '',
				};
				dispatch(setSelectedVesselsForOutputRequest({ outputSelection }));

				lastHydratedRoute.current = currentRouteSignature;
				hydratingFromRoute.current = false;
			}
			return;
		}

		// Skip if we already processed this exact route
		if (lastHydratedRoute.current === currentRouteSignature) {
			// console.log('Hydration Effect: Skipped - already processed this route:', currentRouteSignature);
			return;
		}

		// Skip if store already reflects this route selection.
		const selectionInStore = parseSelectionFromState(selectionData);
		if (selectionsMatch(selectionInStore, selectionFromRoute)) {
			// console.log('Hydration Effect: Skipped - store already matches route selection.');
			lastHydratedRoute.current = currentRouteSignature;
			return;
		}

		// If we already have an active selection in memory (user just picked something)
		// and this is not a back/forward navigation, prefer the in-memory selection to avoid loops.
		// BUT allow hydration on page reload (when we don't have the exact same route in lastHydratedRoute)
		const hasActiveSelection =
			selectionData.selectionType !== ESelectionType.None &&
			selectionData.selectedVesselsList &&
			selectionData.selectedVesselsList.length > 0;
		if (hasActiveSelection && !isRealPopRef.current && lastHydratedRoute.current) {
			// console.log('Hydration Effect: Skipped - active selection exists and not a POP navigation.');
			lastHydratedRoute.current = currentRouteSignature;
			hydratingFromRoute.current = false;
			return;
		}

		// Require vessel data for vessel/ownership/sector selections
		if (
			(type === ESelectionType.Vessel || type === ESelectionType.Ownership || type === ESelectionType.Sector) &&
			!subscribedVessels.length
		) {
			// console.log('Hydration Effect: Skipped - vessel data required but not available.');
			return;
		}

		if (lastHydratedRoute.current === currentRouteSignature) {
			// console.log('Hydration Effect: Skipped - already processed this route (second check):', currentRouteSignature);
			return;
		}

		let hydrated = false;

		// Prevent the push effect from racing while we hydrate from the current URL.
		hydratingFromRoute.current = true;
		// console.log('Hydration Effect: Start hydration from route. hydratingFromRoute.current = true');

		dispatch(setPageSelection({ type, id: param }));
		hydrated = true;
		// console.log('Hydration Effect: Dispatched setPageSelection:', { type, id: param });

		if (type === ESelectionType.Vessel) {
			const imoNumber = param ? Number(param) : NaN;

			if (Number.isNaN(imoNumber)) {
				hydratingFromRoute.current = false;
				return;
			}

			const vessel = subscribedVessels.find((v) => v.imoNumber === imoNumber);

			if (!vessel) {
				hydratingFromRoute.current = false;
				return;
			}

			// console.log('Hydration Effect: Hydrating for Vessel.');
			dispatch(
				setSelectedVesselsForOutputRequest({
					outputSelection: {
						selectedVesselsList: [
							{
								name: vessel.name,
								imoNumber: vessel.imoNumber,
								sectorName: vessel.sector,
								companyName: vessel.companyName,
							},
						],
						outputType: EOutputType.Vessel,
						selectionType: ESelectionType.Vessel,
						selectionName: `${vessel.imoNumber} - ${vessel.name}`,
					},
				}),
			);
			lastHydratedRoute.current = currentRouteSignature;
			hydratingFromRoute.current = false;
			// console.log('Hydration Effect: End hydration. hydratingFromRoute.current = false');
			return;
		}

		if (type === ESelectionType.Port) {
			if (!param) {
				hydratingFromRoute.current = false;
				return;
			}
			// console.log('Hydration Effect: Hydrating for Port.');
			// Split param by '-' to get country and portName
			const paramParts = param.split('_');
			const portName = paramParts[0];
			const country = paramParts.length > 1 ? paramParts[1] : ''; // Join remaining parts in case portName contains '-'
			
			// Validate portName
			if (isNil(portName)) {
				console.error('Invalid portName in param:', param);
				hydratingFromRoute.current = false;
				return;
			}

			// console.log('Parsed port params:', { portName, country });

			dispatch(getPortGeometryByNameAndCountryRequest({ portName, country }));
			lastHydratedRoute.current = currentRouteSignature;
			hydratingFromRoute.current = false;
			// console.log('Hydration Effect: End hydration. hydratingFromRoute.current = false');
			return;
		}

		if (type === ESelectionType.Ownership || type === ESelectionType.Sector) {
			if (!param) {
				hydratingFromRoute.current = false;
				return;
			}

			// console.log('Hydration Effect: Hydrating for Ownership/Sector.');
			const outputSelection = filterGivenVesselsByFilterCriteriaForOutput(subscribedVessels as IVesselSearchForm[], {
				type,
				value: param,
			});

			dispatch(setSelectedVesselsForOutputRequest({ outputSelection }));
			lastHydratedRoute.current = currentRouteSignature;
			hydratingFromRoute.current = false;
			// console.log('Hydration Effect: End hydration. hydratingFromRoute.current = false');
			return;
		}

		if (type === ESelectionType.Portfolio || type === ESelectionType.DraftPortfolio) {
			if (!param) {
				hydratingFromRoute.current = false;
				return;
			}
			// console.log('Hydration Effect: Hydrating for Portfolio.');
			dispatch(displayOutputByPortfolioIdRequest({ id: parseInt(param) }));
			lastHydratedRoute.current = currentRouteSignature;
			hydratingFromRoute.current = false;
			// console.log('Hydration Effect: End hydration. hydratingFromRoute.current = false');
			return;
		}

		if (type === ESelectionType.Filter || type === ESelectionType.DraftFilter) {
			if (!param) {
				hydratingFromRoute.current = false;
				return;
			}
			// console.log('Hydration Effect: Hydrating for Filter.');
			dispatch(displayOutputByFilterIdRequest({ id: parseInt(param) }));
			lastHydratedRoute.current = currentRouteSignature;
			hydratingFromRoute.current = false;
			// console.log('Hydration Effect: End hydration. hydratingFromRoute.current = false');
			return;
		}

		if (hydrated) {
			lastHydratedRoute.current = currentRouteSignature;
			hydratingFromRoute.current = false;
			// console.log('Hydration Effect: End hydration (generic). hydratingFromRoute.current = false');
		}
	}, [dispatch, selectionData, selectionFromRoute, subscribedVessels, location.pathname]);

	// Handle feature changes from Redux (e.g., when OutputMenu dispatches setFeature)
	useEffect(() => {
		// console.log('Feature Change Effect: Redux to URL');
		if (!subscribedVessels || subscribedVessels.length === 0) {
			// console.log('Feature Change Effect: Skipped - no subscribed vessels.');
			return;
		}

		// Skip if we're currently hydrating from route to avoid conflicts
		if (hydratingFromRoute.current) {
			// console.log('Feature Change Effect: Skipped - hydrating from route.');
			return;
		}

		// Skip if this feature change came from a route change (not from Redux action)
		if (pageContext.feature === feature) {
			// console.log('Feature Change Effect: Skipped - pageContext.feature already matches route feature.');
			return;
		}

		const currentRouteSignature = `${location.pathname}`;

		if (isRealPopRef.current && currentRouteSignature !== '/') {
			// console.log('Feature Change Effect: Skipped - real back navigation.');
			// isRealPopRef.current = false; // 👈 consume it
			return;
		}

		// Navigate to the appropriate URL based on current selection
		if (pageContext.feature && !isRealPopRef.current) {
			const selectionInStore = parseSelectionFromState(selectionData);

			let newFeature = pageContext.feature;

			const pathname = buildPathname(newFeature, undefined, undefined, selectionInStore);

			// console.log('Feature Change Effect: Navigating due to feature change.', { pathname, replace: false });
			// Use replace to avoid creating intermediate routes in history
			navigate(pathname, { replace: false });
		} else if (!pageContext.feature && feature) {
			// Feature was cleared (e.g., dashboard close button clicked)
			const selectionInStore = parseSelectionFromState(selectionData);

			if (currentRouteSignature === '/') {
				return;
			}

			// Navigate based on current selection state
			if (selectionInStore.type && selectionInStore.param) {
				// If we have a selection, go to direct selection route (e.g., /vessel/123)
				const pathname = `/${selectionInStore.type.toLowerCase()}/${encodeParam(selectionInStore.param)}`;

				navigate(pathname, { replace: false });
			} else {
				// No feature, go to root
				navigate('/', { replace: false });
			}
		}
	}, [pageContext.feature, feature, selectionData, navigate, subscribedVessels, location.pathname]);

	// Handle tab/subtab changes from Redux
	useEffect(() => {
		// console.log('Tab/Subtab Change Effect: Redux to URL');
		if (!subscribedVessels || subscribedVessels.length === 0) return;
		// For POP navigation (back/forward), skip to avoid loops
		if (isRealPopRef.current) {
			// console.log('Tab/Subtab Change Effect: Skipped - POP navigation.');
			return;
		}
		if (hydratingFromRoute.current) {
			// console.log('Tab/Subtab Change Effect: Skipped - hydrating from route.');
			return;
		}

		if (!pageContext.tab) {
			// console.log('Tab/Subtab Change Effect: Skipped - no pageContext.tab.');
			return;
		}

		// Skip if this tab/subtab change came from a route change (not from Redux action)
		if (pageContext.feature === feature && pageContext.tab === tab && pageContext.subtab === subtab) {
			// console.log('Tab/Subtab Change Effect: Skipped - tab/subtab already match route.');
			return;
		}

		// Build new URL with current feature, selection, and new tab/subtab
		const selectionInStore = parseSelectionFromState(selectionData);
		const pathname = buildPathname(pageContext.feature || feature, pageContext.tab, pageContext.subtab, selectionInStore);

		const tabChanged = pageContext.tab !== tab;
		const subtabChanged = pageContext.subtab !== subtab;

		let replaceValue = false;
		if (
			feature !== pageContext.feature ||
			(feature === pageContext.feature &&
				(!tab ||
					//(tab && tabChanged) ||
					(tabChanged && pageContext.subtab && subtabChanged)))
			// || !subtab ||
			// (subtab && subtab === pageContext.subtab)
		) {
			replaceValue = true;
		}

		// console.log('Tab/Subtab Change Effect: Navigating due to tab/subtab change.', { pathname, replace: replaceValue });
		navigate(pathname, { replace: replaceValue }); // Use push for tab history
	}, [
		pageContext.tab,
		pageContext.subtab,
		tab,
		subtab,
		pageContext.feature,
		feature,
		selectionData,
		navigate,
		subscribedVessels,
	]);

	// Push redux-driven selection changes into the URL so browser history keeps them.
	useEffect(() => {
		// console.log('Selection Change Effect: Redux to URL');
		if (!subscribedVessels || subscribedVessels.length === 0) {
			// console.log('Selection Change Effect: Skipped - no subscribed vessels.');
			return;
		}
		const selectionInStore = parseSelectionFromState(selectionData);
		// console.log('Selection Change Effect: Selection in store:', selectionInStore);

		// Skip if we're currently hydrating from route
		if (hydratingFromRoute.current) {
			// console.log('Selection Change Effect: Skipped - hydrating from route.');
			return;
		}

		// For POP navigation (back/forward), be more conservative
		if (isRealPopRef.current) {
			// console.log('Selection Change Effect: Skipped - POP navigation.');
			// For back/forward navigation, let the browser handle it and only intervene if absolutely necessary
			// Don't create new navigation entries that could cause loops
			return;
		}

		// Update our reference for next comparison
		lastSelectionInStore.current = selectionInStore;

		// If no selection in store, only navigate if we currently have a selection in URL
		if (!selectionInStore.type) {
			if (selectionFromRoute.type) {
				// If we're on a pure selection route, go to root
				// If we're on a feature+selection route, go to just the feature
				const targetPath = feature && !normalizeSelectionType(feature) ? `/${feature.toLowerCase()}` : '/';
				navigate(targetPath, { replace: false });
			}
			return;
		}

		// Skip if selections already match
		if (selectionsMatch(selectionInStore, selectionFromRoute)) {
			// console.log('Selection Change Effect: Skipped - selection in store already matches route.');
			return;
		}

		// Build the new pathname - always navigate when there's a selection change
		const pathname = buildPathname(feature, tab, subtab, selectionInStore);
		const currentUrl = location.pathname;

		if (pathname.toLowerCase() !== currentUrl.toLowerCase()) {
			navigate(pathname, { replace: false });
		}
	}, [feature, tab, subtab, location.pathname, navigate, selectionData, selectionFromRoute, subscribedVessels]);

	return null;
};

export default RouteStateManager;
